// Sidebar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiHome, FiSearch, FiTag, FiCheck, FiX,
  FiGrid, FiUpload, FiDollarSign, FiLogOut,
  FiSettings, FiHeadphones, FiUsers, FiShoppingCart,
  FiClock, FiPackage, FiAlertCircle, FiClipboard,
  FiFileText, FiBox, FiLayers, FiList, FiPlus,
  FiFilter, FiCommand, FiDatabase, FiLock, FiShield,
  FiMail
} from 'react-icons/fi';
import Header from './Header';
import { FileChartPie } from 'lucide-react';
import usePermission from '../hooks/usePermission';
import { FaEnvelope } from 'react-icons/fa';
import { FaUserGroup } from 'react-icons/fa6';
import { UserOutlined } from '@ant-design/icons';
import { SiXero } from 'react-icons/si';

const MenuItem = ({ icon: Icon, to, children, border, iconColor = 'text-golden-blue', count, onClick, hasSubmenu = false, isSubmenuOpen = false, onSubmenuToggle, permission }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  const { hasPermission, isAdmin } = usePermission();

  // Check if the user has permission to see this menu item
  const hasAccess = (!permission || permission === '')
    ? true
    : (isAdmin || hasPermission(permission));

  if (!hasAccess) return null;

  return (
    <li style={{ marginTop: border ? '-12px !important' : '' }}>
      <Link
        to={hasSubmenu ? '#' : to}
        className={`flex items-center justify-between ${!border ? 'p-2' : 'pl-2'} text-sm ${isActive ? 'text-golden-yellow' : 'text-white'} hover:bg-golden-blue hover:bg-opacity-20 rounded transition duration-150 ease-in-out`}
        onClick={(e) => {
          if (hasSubmenu) {
            e.preventDefault();
            onSubmenuToggle && onSubmenuToggle();
          } else {
            onClick && onClick();
          }
        }}
      >
        <div className="flex items-center">
          <Icon className={`h-4 w-4 mr-2 ${isActive ? 'text-golden-yellow' : iconColor}`} />
          {border ? <p style={{ height: 30 }} className='border-l text-golden-navy pr-2'> | </p> : null}
          <span>{children}</span>
        </div>
        <div className="flex items-center">
          {count !== undefined && (
            <span className="bg-golden-light text-golden-navy px-2 py-0.5 rounded-full text-xs mr-2">
              {count}
            </span>
          )}
          {hasSubmenu && (
            <span className={`transition-transform duration-200 ${isSubmenuOpen ? 'transform rotate-180' : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </span>
          )}
        </div>
      </Link>
    </li>
  );
};

const Sidebar = ({ serviceMode,isOpen, toggleSidebar, orderStats, logout, dashboardData = {}, productCount = 0 }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const { isAdmin, hasPermission, hasAnyPermission } = usePermission();

  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024 && isOpen) {
      toggleSidebar();
    }
    // Close the drawer after mobile navigation; desktop keeps its fixed sidebar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleExpandMenu = (menuKey) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  // Determine if a menu item should be shown based on permissions
  const shouldShowMenuItem = (requiredPermission) => {
    if (!requiredPermission || requiredPermission === '') return true;
    // if (isAdmin) return true;
    return hasPermission(requiredPermission);
  };

  // Determine if a section should be shown based on its items' permissions
  const shouldShowSection = (section) => {
    // if (isAdmin) return true;

    // If section has direct permission requirement
    if (section.permission) {
      if (hasPermission(section.permission)) return true;
    }

    // Check if any items in the section are visible
    const hasVisibleItems = section.items.some(item =>
      shouldShowMenuItem(item.permission)
    );

    return hasVisibleItems;
  };

  // Update counts from dashboard data if available
  useEffect(() => {
    if (dashboardData && Object.keys(dashboardData).length > 0) {
      // Update admin menu items with actual counts
      setAdminMenuItems(prev => {
        const updatedItems = [...prev];

        // Update Products count
        if (updatedItems[0] && updatedItems[0].items) {
          const productItem = updatedItems[0].items.find(i => i.label === "Products");
          if (productItem) productItem.count = dashboardData.products || productItem.count;
        }

        // Update other counts similarly
        return updatedItems;
      });
    }
  }, [dashboardData]);

  const orderMenuItems = [
    {
      label: 'All Orders',
      icon: FiClock,
      path: '/orders/active',
      count: orderStats?.active || 0,
      permission: 'view_orders'
    },
    /*  { 
       label: 'Completed Orders', 
       icon: FiCheck, 
       path: '/orders/completed',
       count: orderStats?.completed || 0,
       permission: 'view_orders'
     },
     { 
       label: 'Failed/Cancelled', 
       icon: FiX, 
       path: '/orders/failed',
       count: orderStats?.failed || 0,
       permission: 'view_orders'
     } */
  ];

  // Admin specific menu items - SORTED IN REQUESTED ORDER
  const [adminMenuItems, setAdminMenuItems] = useState([
    // 1. Dashboard - handled separately

    // 2. Order Management
    {
      title: "Order Management",
      key: "orders",
      permission: "admin_view_orders",
      items: [
        { label: "All Orders", icon: FiShoppingCart, path: "/admin/all-orders", permission: "admin_view_orders" },
      ]
    },

    // 3. Property Management
    {
      title: "Property Management",
      key: "properties",
      permission: "manage_properties",
      items: [
        { label: "All Properties", icon: FiHome, path: "/admin/properties", permission: "manage_properties" }
      ]
    },

    // 4. User Management
    {
      title: "User Management",
      key: "users",
      permission: "view_users",
      items: [
        { label: "All Users", icon: FiUsers, path: "/admin/all-users", permission: "view_users" },
        { label: "Waiting Approvals", icon: FiUsers, path: "/admin/waiting-users", permission: "manage_users" },
      ]
    },

    // 5. Product Management
    {
      title: "Product Management",
      key: "products",
      permission: "manage_products",
      items: [
        { label: "All Products", icon: FiList, path: "/admin/all-products", permission: "manage_products" },
        { label: "Categories", icon: FiFilter, path: "/admin/products/categories", permission: "manage_categories" },
      ]
    },

    // 6. Pack Management
    {
      title: "Pack Management",
      key: "packs",
      permission: "manage_packs",
      items: [
        { label: "All Packs", icon: FiList, path: "/admin/all-packs", permission: "manage_packs" },
      ]
    },

    // 7. Finance
    {
      title: "Finance",
      key: "finance",
      permission: "admin_view_orders",
      items: [
        { label: "Invoices", icon: FiFileText, path: "/admin/invoices", permission: "admin_view_orders" },
      ]
    },

    // Integrations
    {
      title: "Integrations",
      key: "integrations",
      permission: "admin_view_orders",
      items: [
        { label: "Xero Accounts", icon: SiXero, path: "/admin/integrations/xero", permission: "admin_view_orders" },
      ]
    },

    // 8. Payment Management
    {
      title: "Payment Management",
      key: "payments",
      permission: "manage_payments",
      items: [
        { label: "All Payments", icon: FiDollarSign, path: "/admin/payments", permission: "manage_payments" },
      ]
    },

    // 9. Reports
    {
      title: "Reports",
      key: "reports",
      permission: "admin_view_reports",
      items: [
        { label: "Sales Reports", icon: FiFileText, path: "/admin/reports/sales", permission: "admin_view_reports" },
      ]
    },

    // 9. Messaging
    {
      title: "Messaging",
      key: "messaging",
      permission: "admin_messaging",
      items: [
        { label: "All Messages", icon: FiMail, path: "/admin/messages", permission: "admin_messaging" },
        { label: "Inbox", icon: FiMail, path: "/admin/messages/inbox", permission: "admin_messaging" },
        { label: "User Groups", icon: FaUserGroup, path: "/admin/user-groups", permission: "admin_messaging" },
            { label: "Contact Forms", icon: FiMail, path: "/admin/contact-forms", permission: "admin_messaging" },

      ]
    },

    // 10. System Settings
    {
      title: "System Settings",
      key: "settings",
      permission: "manage_system",
      items: [
        { label: "Notification Settings", icon: FiMail, path: "/admin/notification-settings", permission: "manage_system" },
        { label: "Stripe Settings", icon: FiDollarSign, path: "/admin/stripe-settings", permission: "manage_system" },
        { label: "Roles", icon: FiShield, path: "/admin/roles", permission: "manage_roles" },
        { label: "Permissions", icon: FiLock, path: "/admin/permissions", permission: "manage_permissions" },
      ]
    },

    {
      title: 'My Profile',
      key: "profile",
      permission: 'view_profile',
      items: [
        { label: 'Profile', icon: FiSettings, path: '/profile', permission: 'view_profile' },
      ]
    },
  ]);

  return (
    <>
      <aside style={{ height: '95vh' }} className={`${serviceMode?'bg-golden-navy':'bg-golden-navy text-white'}  w-56 flex flex-col transition-all duration-300 ease-in-out fixed lg:relative ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 z-20`}>
        <Header isSidebar={true} toggleSidebar={toggleSidebar} />
        <div className="flex-grow overflow-y-auto p-3 text-sm">
          {serviceMode ? (
            <>
              <h2 className="text-base font-bold mb-3">Service Panel</h2>
              <nav>
                <ul className="space-y-1">
                  <MenuItem to="/service-dashboard" icon={FiHome}>Dashboard</MenuItem>
                  <MenuItem to="/orders/active" icon={FiShoppingCart}>Active Orders</MenuItem>
                  {/* Dodaj još ako imaš posebne service rute */}
                </ul>
              </nav>
            </>
          ) : isAdmin ? (
            // Admin Menu
            <>
              {/* 1. Dashboard - First in order */}
              <h2 className="text-base font-bold mb-3">Dashboard</h2>
              <nav>
                <ul className="space-y-0">
                  <MenuItem to="/admin/dashboard" icon={FiHome}>Dashboard</MenuItem>
                </ul>
              </nav>

              {/* 2-10. Other sections in sorted order */}
              {adminMenuItems.map((section, idx) => (
                shouldShowSection(section) && (
                  <div key={`section-${section.key}`} className="mt-1">
                    <h2 className="text-base font-bold mb-3">{section.title}</h2>
                    <nav>
                      <ul className="space-y-0 pl-2">
                        {section.items.map((item, itemIdx) => (
                          shouldShowMenuItem(item.permission) && (
                            <MenuItem
                              key={`item-${section.key}-${itemIdx}`}
                              to={item.path}
                              icon={item.icon}
                              count={item.count}
                              permission={item.permission}
                            >
                              {item.label}
                            </MenuItem>
                          )
                        ))}
                      </ul>
                    </nav>
                  </div>
                )
              ))}
            </>
          ) : (
            // Regular User Menu
            <>
              <h2 className="text-base font-bold mb-3">Main Menu</h2>
              <nav>
                <ul className="space-y-1">
                  <MenuItem to="/" icon={FiHome}>Dashboard</MenuItem>
                  <MenuItem to="/properties" icon={FiHome} permission="view_properties">Properties</MenuItem>
                  {shouldShowMenuItem('view_orders') && (
                    <MenuItem
                      to="/orders"
                      icon={FiShoppingCart}
                      permission="view_orders"
                      hasSubmenu={true}
                      isSubmenuOpen={expandedMenus['orders']}
                      onSubmenuToggle={() => toggleExpandMenu('orders')}
                    >
                      Orders
                    </MenuItem>
                  )}

                  {expandedMenus['orders'] && (
                    <div className="ml-6 mt-1 space-y-1">
                      {orderMenuItems.map((item, idx) => (
                        shouldShowMenuItem(item.permission) && (
                          <MenuItem
                            key={`order-item-${idx}`}
                            to={item.path}
                            icon={item.icon}
                            count={item.count}
                            permission={item.permission}
                            border={true}
                          >
                            {item.label}
                          </MenuItem>
                        )
                      ))}
                    </div>
                  )}
                </ul>
              </nav>

              <nav>
                <ul className="space-y-1">
                  <MenuItem to="/pricing" icon={FiDollarSign}>Pricing</MenuItem>
                </ul>
              </nav>

              <nav>
                <ul className="space-y-1">
                  <MenuItem to="/reports" icon={FileChartPie} permission="view_reports">Reports</MenuItem>
                </ul>
              </nav>

              <nav>
                <ul className="space-y-1">
                  <MenuItem to="/inbox" permission="view_inbox" icon={FaEnvelope}>Inbox</MenuItem>
                </ul>
              </nav>

              <h2 className="text-base font-bold mt-6 mb-3">Other Menu</h2>
              <nav>
                <ul className="space-y-1">
                  <MenuItem to="/support" icon={FiHeadphones}>Support (Coming Soon)</MenuItem>
                  <MenuItem to="/refer" icon={FiUsers}>Refer a Friend</MenuItem>
                </ul>
              </nav>
            </>
          )}

          <h2 className="text-base font-bold mt-6 mb-3"></h2>
          <nav>
            <ul className="space-y-3">
              <span>
                <MenuItem to="/profile" icon={UserOutlined}>My Profile</MenuItem>
              </span>
            </ul>
          </nav>
          <nav>
            <ul className="space-y-3">
              <span onClick={logout}>
                <MenuItem to="#logout" icon={FiLogOut}>Logout</MenuItem>
              </span>
            </ul>
          </nav>
        </div>
      </aside>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-10 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}
    </>
  );
};

export default Sidebar;
